import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import {
  buildTaskSchedulePayloadFromRow,
  buildTaskScheduleStatusPayload,
  mapTaskScheduleFieldErrors,
  mapTaskScheduleResponseToDto,
  mapTaskSchedulesResponseToDto,
  validateTaskSchedulePayload,
} from '../dto/system-tasks.dto.js';
import { buildTaskRowsModel } from '../model/system-tasks.model.js';

function hasTaskChanges(row) {
  return String(row?.draftCronExpression || '').trim() !== String(row?.cronExpression || '').trim();
}

export function useSystemTasks() {
  const { token } = useAuth();

  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [savingTask, setSavingTask] = useState('');
  const [savingAction, setSavingAction] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formErrors, setFormErrors] = useState({});

  const applyTaskUpdate = useCallback((task, updatedTask, fallbackPatch = {}) => {
    setRows((prev) => prev.map((row) => {
      if (row.task !== task) {
        return row;
      }

      const source = updatedTask || {
        ...row,
        ...fallbackPatch,
      };

      return {
        task: source.task || row.task,
        label: source.label || row.label,
        cronExpression: source.cronExpression ?? row.cronExpression,
        draftCronExpression: source.cronExpression ?? row.draftCronExpression,
        nextRunAt: source.nextRunAt ?? row.nextRunAt,
        lastRunAt: source.lastRunAt ?? row.lastRunAt,
        isActive: source.isActive ?? row.isActive,
      };
    }));
  }, []);

  const loadTasks = useCallback(async () => {
    setIsLoading(true);
    setError('');
    setSuccess('');
    setFormErrors({});

    try {
      const response = await adminApi.getTaskSchedules(token);
      const dto = mapTaskSchedulesResponseToDto(response);
      setRows(buildTaskRowsModel(dto));
    } catch (requestError) {
      if (requestError?.status === 404) {
        setRows([]);
      } else {
        setRows([]);
        setError(requestError.message || 'Failed to load tasks');
      }
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const onCronExpressionChange = useCallback((task, value) => {
    setRows((prev) => prev.map((row) => (
      row.task === task
        ? { ...row, draftCronExpression: value }
        : row
    )));

    setFormErrors((prev) => {
      if (!prev[task]) {
        return prev;
      }

      const next = { ...prev };
      delete next[task];
      return next;
    });
  }, []);

  const resetTask = useCallback((task) => {
    if (savingTask) {
      return;
    }

    setRows((prev) => prev.map((row) => (
      row.task === task
        ? { ...row, draftCronExpression: row.cronExpression }
        : row
    )));

    setFormErrors((prev) => {
      if (!prev[task]) {
        return prev;
      }

      const next = { ...prev };
      delete next[task];
      return next;
    });
  }, [savingTask]);

  const saveTask = useCallback(async (task) => {
    if (savingTask) {
      return;
    }

    const targetRow = rows.find((row) => row.task === task);
    if (!targetRow || !hasTaskChanges(targetRow)) {
      return;
    }

    const payload = buildTaskSchedulePayloadFromRow(targetRow);
    const nextErrors = validateTaskSchedulePayload(payload);
    if (Object.keys(nextErrors).length > 0) {
      setFormErrors((prev) => ({ ...prev, [task]: nextErrors }));
      setError('Please fix validation errors.');
      setSuccess('');
      return;
    }

    setSavingTask(task);
    setSavingAction('schedule');
    setError('');
    setSuccess('');
    setFormErrors((prev) => {
      if (!prev[task]) {
        return prev;
      }

      const next = { ...prev };
      delete next[task];
      return next;
    });

    try {
      const response = await adminApi.updateTaskSchedule(token, task, payload);
      const updatedTask = mapTaskScheduleResponseToDto(response);
      applyTaskUpdate(task, updatedTask, {
        cronExpression: payload.cronExpression,
      });

      setSuccess('Task schedule updated.');
    } catch (requestError) {
      setFormErrors((prev) => ({
        ...prev,
        [task]: mapTaskScheduleFieldErrors(requestError),
      }));
      setError(requestError.message || 'Failed to update task schedule');
    } finally {
      setSavingTask('');
      setSavingAction('');
    }
  }, [applyTaskUpdate, rows, savingTask, token]);

  const toggleTaskStatus = useCallback(async (task) => {
    if (savingTask) {
      return;
    }

    const targetRow = rows.find((row) => row.task === task);
    if (!targetRow) {
      return;
    }

    const nextIsActive = !targetRow.isActive;

    setSavingTask(task);
    setSavingAction('status');
    setError('');
    setSuccess('');

    try {
      const response = await adminApi.updateTaskScheduleStatus(
        token,
        task,
        buildTaskScheduleStatusPayload(nextIsActive),
      );
      const updatedTask = mapTaskScheduleResponseToDto(response);
      applyTaskUpdate(task, updatedTask, {
        isActive: nextIsActive,
      });
      setSuccess(`Task ${nextIsActive ? 'activated' : 'deactivated'}.`);
    } catch (requestError) {
      setError(requestError.message || 'Failed to update task status');
    } finally {
      setSavingTask('');
      setSavingAction('');
    }
  }, [applyTaskUpdate, rows, savingTask, token]);

  return {
    rows,
    isLoading,
    isSaving: Boolean(savingTask),
    savingTask,
    savingAction,
    error,
    success,
    formErrors,
    onCronExpressionChange,
    resetTask,
    saveTask,
    toggleTaskStatus,
  };
}
